<script setup lang="ts">
/**
 * Main clients table tab.
 *
 * Infinite-scrolls the `/clients` endpoint and maps each page into `ClientRow`
 * for `DynamicTable`. Row click opens `ClientCard` in the info panel;
 * `useClientCardStore` and `useAddClientFormStore` carry selection and
 * create/edit state. `clearInfoViewSelections` enforces mutual exclusion when
 * the user switches panels.
 *
 * `genderLetter` reduces API gender strings (`FEMALE` / `MALE`) to a single
 * display glyph. `isHeaderVisible` drives the sticky-header collapse animation.
 */
import { computed, markRaw, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/vue-query'
import DynamicTable from '@/components/table/DynamicTable.vue'
import type { Column } from '@/components/table/DynamicTable.vue'
import AddButton from '@/components/table/AddButton.vue'
import TableSearchInput from '@/components/table/TableSearchInput.vue'
import ClientNameCell from '@/components/table/cell/ClientNameCell.vue'
import ClientMobileLeftCell from '@/components/table/cell/ClientMobileLeftCell.vue'
import ClientMobileRightCell from '@/components/table/cell/ClientMobileRightCell.vue'
import RowIndexCell from '@/components/table/cell/RowIndexCell.vue'
import LastVisitCell from '@/components/table/cell/LastVisitCell.vue'
import MoneyCell from '@/components/table/cell/MoneyCell.vue'
import DebtCell from '@/components/table/cell/DebtCell.vue'
import PositiveIntegerCell from '@/components/table/cell/PositiveIntegerCell.vue'
import {
  ACTIVE_CLIENTS_RECENCY_MONTHS,
  clientBalanceMinorUnits,
  clientDebtMinorUnits,
  deleteClient,
  fetchClients,
  type Client,
  type ClientListFilter,
} from '@/api/clients'
import { moneyColumnLabel } from '@/utils/formatMoney'
import { clientsPollingInterval } from '@/config/api'
import { useClientCardStore } from '@/stores/client_card_store'
import { useAddClientFormStore } from '@/stores/add_client_form_store'
import { clearInfoViewSelections } from '@/stores/info_view_selection'
import { useIsVisible } from '@/composables/useIsVisible'
import { useIsMobile } from '@/composables/useIsMobile'
import { usePermissions } from '@/composables/usePermissions'
import { ResourceAccessRole } from '@/models/resource_access_role'
import ClientCardMobileScreen from '@/components/main/clientCard/ClientCardMobileScreen.vue'
import GroupNameCell from '@/components/table/cell/GroupNameCell.vue'
import GroupScheduleCell from '@/components/table/cell/GroupScheduleCell.vue'
import GroupRoomCell from '@/components/table/cell/GroupRoomCell.vue'
import GroupPerformersCell from '@/components/table/cell/GroupPerformersCell.vue'
import GroupServiceTypesCell from '@/components/table/cell/GroupServiceTypesCell.vue'
import GroupStateCell from '@/components/table/cell/GroupStateCell.vue'
import GroupMobileCell from '@/components/table/cell/GroupMobileCell.vue'
import GroupMobileScreen from '@/components/main/groupCard/GroupMobileScreen.vue'
import { deleteGroup, fetchGroups, isGroupActive, type Group } from '@/api/groups'
import { groupsPollingInterval } from '@/config/api'
import { useGroupCardStore } from '@/stores/group_card_store'
import { useAddGroupFormStore } from '@/stores/add_group_form_store'
import { useGroupDisplay } from '@/composables/useGroupDisplay'
import { useAllPerformers } from '@/composables/useAllPerformers'
import { useAllBusinessServiceTypes } from '@/composables/useAllBusinessServiceTypes'
import { useAllBookingTypes } from '@/composables/useAllBookingTypes'
import { formatStartDate } from '@/utils/formatStartDate'

const clientCardStore = useClientCardStore()
const addClientFormStore = useAddClientFormStore()
const groupCardStore = useGroupCardStore()
const addGroupFormStore = useAddGroupFormStore()
const { isMobile } = useIsMobile()
const { t } = useI18n()
const { hasRole } = usePermissions()

type Pane = 'clients' | 'groups'
const activePane = ref<Pane>('clients')

const headerRef = ref<HTMLElement | null>(null)
const isHeaderVisible = useIsVisible(headerRef)

const mobileScrollSentinel = ref<HTMLElement | null>(null)
const mobileScrollSentinelVisible = useIsVisible(mobileScrollSentinel)
const isMobileScrolled = computed(() => isMobile.value && !mobileScrollSentinelVisible.value)

const searchQuery = ref('')
// Debounced value from TableSearchInput; part of the infinite-query key so a
// new search restarts pagination from the first page instead of filtering
// already-loaded rows.

const activeFilter = ref<ClientListFilter>('active')
// Segmented-control selection; 'active' is the default so newly opened tabs
// don't show clients who registered but never returned.

// Computed (not a plain const) so labels re-translate when the user
// switches language.
const CLIENT_FILTERS = computed<ReadonlyArray<{ value: ClientListFilter; label: string }>>(() => [
  {
    value: 'active',
    label: t('tabs.clients.filterActive', { months: ACTIVE_CLIENTS_RECENCY_MONTHS }),
  },
  { value: 'negative', label: t('tabs.clients.filterNegative') },
  { value: 'all', label: t('tabs.clients.filterAll') },
])

/**
 * Maps API gender strings to a single letter for the table.
 */
function genderLetter(raw: string): string {
  const u = raw.trim().toUpperCase()
  if (u === 'FEMALE' || u === 'F') return 'f'
  if (u === 'MALE' || u === 'M') return 'm'
  return u.length > 0 ? u[0]!.toLowerCase() : '–'
}

interface ClientRow {
  // Row shape for the clients table (mapped from API + placeholders).

  id: string
  // Person id of this client; powers `rowKey` for selection state and is
  // sent to `useClientCardStore.selectClient` when the row is clicked so the
  // info card can fetch this client's data.

  rowNumber: number
  // 1-based index in the current loaded list (all pages flattened).

  name: string
  // Client display name.

  gender: string
  // Single-letter gender (`f` / `m`), rendered as a glyph inside the Name cell's secondary line.

  balance?: number
  // Balance in minor currency units when `current_balance` is present; else cell shows placeholder.

  debt?: number
  // Debt in minor currency units, derived from `latest_balance`; else cell shows placeholder.
  // See `DebtCell` — non-negative `latest_balance` renders as 0 (no debt), not the raw value.

  lastVisit?: string
  // Last visit (ISO datetime) from API `last_visit_datetime` when present.

  startDate?: string
  // Start datetime string, folded into the Name cell's two-line secondary block when present.

  businessServiceCount?: number
  // Number of business services visited by the client.

  _raw: Client
  // Full API record kept alongside the display-ready fields above. The table
  // cells only need a subset (name, balance, lastVisit, …), but the edit form
  // also requires fields that are not shown in the table — phone, email, roles,
  // start_datetime. Storing the raw object here avoids duplicating those fields
  // in ClientRow and lets the row edit handler read whatever it needs directly.
}

// Column widths sum to ~48rem (≈ `--width-middle-column` minus the
// article's 4rem horizontal padding). Held constant across tabs so the
// flanks keep their on-screen width as the user navigates between tabs.
//
// Computed (not a plain const) so labels re-translate when the user
// switches language.
const columns = computed<Column<ClientRow>[]>(() => [
  {
    key: 'rowNumber',
    label: '#',
    component: markRaw(RowIndexCell),
    props: (row) => ({ index: row.rowNumber }),
    width: '4rem',
  },
  {
    key: 'name',
    label: t('common.name'),
    component: markRaw(ClientNameCell),
    props: (row) => ({
      name: row.name,
      gender: row.gender,
      startDate: row.startDate,
    }),
    width: '15rem',
  },
  {
    key: 'balance',
    label: moneyColumnLabel(t('common.balance')),
    component: markRaw(MoneyCell),
    props: (row) => ({ amount: row.balance, emptyPlaceholder: '–' }),
    width: '6rem',
    align: 'center',
  },
  {
    key: 'debt',
    label: moneyColumnLabel(t('common.debt')),
    component: markRaw(DebtCell),
    props: (row) => ({ amount: row.debt, emptyPlaceholder: '–' }),
    width: '6rem',
    align: 'center',
  },
  {
    key: 'businessServiceCount',
    label: t('tabs.clients.colVisitsTotal'),
    component: markRaw(PositiveIntegerCell),
    props: (row) => ({
      value: row.businessServiceCount,
      emptyPlaceholder: '–',
    }),
    width: '4rem',
    align: 'center',
  },
  {
    key: 'lastVisit',
    label: t('tabs.clients.colLastVisit'),
    component: markRaw(LastVisitCell),
    props: (row) => ({ date: row.lastVisit, emptyPlaceholder: '–' }),
    width: '13rem',
  },
])

const mobileColumns: Column<ClientRow>[] = [
  {
    key: 'name',
    label: '',
    component: markRaw(ClientMobileLeftCell),
    props: (row) => ({
      index: row.rowNumber,
      name: row.name,
      gender: row.gender,
      startDate: row.startDate,
    }),
  },
  {
    key: 'balance',
    label: '',
    component: markRaw(ClientMobileRightCell),
    props: (row) => ({
      balance: row.balance,
      debt: row.debt,
      lastVisit: row.lastVisit,
      businessServiceCount: row.businessServiceCount,
    }),
    width: '11rem',
    align: 'right',
  },
]

const {
  data: queryData,
  isPending,
  isError,
  error,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
} = useInfiniteQuery({
  queryKey: ['clients', searchQuery, activeFilter],
  queryFn: ({ pageParam }) => fetchClients(pageParam, searchQuery.value, activeFilter.value),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) => (lastPage.has_next_page ? lastPage.next_cursor : undefined),
  refetchInterval: clientsPollingInterval,
  // Pauses polling when the browser tab is hidden, so it does not hammer the API while the user is elsewhere.
  refetchIntervalInBackground: false,
  // Each search/filter change is a new queryKey, which would otherwise flip
  // `isPending` back to true and swap the table for "Loading clients…" on
  // every keystroke. `keepPreviousData` keeps rendering the previous result
  // set until the new one arrives, so the table only updates in place.
  placeholderData: keepPreviousData,
})

/**
 * Table rows derived from the infinite-query cache: every loaded page is
 * flattened in order so “load more” appends rows without resetting indices.
 *
 * The 'negative' preset's backend filter (`balance: 'lt:0'`) matches each
 * client's *latest balance snapshot*, not `latest_balance` (balance as of
 * this month's start plus payments made so far this month) — the same value
 * the Debt column renders. A client can have a negative snapshot from an
 * earlier month yet a non-negative `latest_balance` because they've since
 * paid it off, so the backend filter alone can surface rows the Debt column
 * shows as "0" (no debt). `negativeDebtOnly` re-filters by the same
 * `clientDebtMinorUnits` value the Debt column uses, so the button only ever
 * lists clients actually carrying a negative debt.
 *
 * `rowNumber` is a 1-based running index across all pages (incremented once
 * per client, not per page), so the “#” column stays stable while scrolling.
 *
 * `startDate` (`start_datetime`) and `lastVisit` (`last_visit_datetime`) are
 * only set when the API sends a non-empty trimmed string; otherwise they stay
 * `undefined` so the downstream cells hide the secondary line (start date,
 * inside `ClientNameCell`) or show the empty placeholder (last visit).
 *
 * Balance comes from `current_balance.amount` via `clientBalanceMinorUnits`;
 * debt comes from `latest_balance` via `clientDebtMinorUnits` — both in
 * `@/api/clients`.
 */
const data = computed<ClientRow[]>(() => {
  const pages = queryData.value?.pages
  if (!pages?.length) return []
  const flatItems = pages.flatMap((page) => page.items ?? [])
  const negativeDebtOnly =
    activeFilter.value === 'negative'
      ? flatItems.filter((c) => {
          const debt = clientDebtMinorUnits(c)
          return debt !== undefined && debt < 0
        })
      : flatItems
  let index = 0
  return negativeDebtOnly.map((c) => {
    index += 1
    const startRaw = c.start_datetime
    const startDate = typeof startRaw === 'string' && startRaw.trim() !== '' ? startRaw : undefined
    const visitRaw = c.last_visit_datetime
    const lastVisit = typeof visitRaw === 'string' && visitRaw.trim() !== '' ? visitRaw : undefined
    const rawCount = c.business_service_count
    const businessServiceCount =
      typeof rawCount === 'number' &&
      Number.isFinite(rawCount) &&
      Number.isInteger(rawCount) &&
      rawCount >= 0
        ? rawCount
        : undefined
    return {
      id: c.id,
      rowNumber: index,
      name: c.name,
      gender: genderLetter(c.gender),
      balance: clientBalanceMinorUnits(c),
      debt: clientDebtMinorUnits(c),
      lastVisit,
      startDate,
      businessServiceCount,
      _raw: c,
    }
  })
})

/**
 * Stable row key for the clients table — the API client id. Combined with
 * `selectedRowKey` from the pinia store this lets `DynamicTable` highlight
 * the client whose card is currently open in `InfoViewLayout`.
 */
function rowKeyFn(row: unknown): string {
  return (row as ClientRow).id
}

/**
 * Click handler attached to every body row. Updates the pinia store, which
 * in turn triggers `InfoViewLayout` to render `<ClientCard>` for the
 * selected client. Re-clicking the same row is a no-op via the store's
 * idempotent `selectClient` action so the card doesn't flash.
 */
function onRowClick(row: unknown) {
  clearInfoViewSelections('client')
  clientCardStore.selectClient((row as ClientRow).id)
}

const selectedRowKey = computed<string | null>(() => clientCardStore.selectedClientId)

function onAddClient() {
  clearInfoViewSelections('addClientForm')
  addClientFormStore.open()
}

const queryClient = useQueryClient()
const deleteMutation = useMutation({ mutationFn: (id: string) => deleteClient(id) })

function onRowDeleteHandler(row: unknown): void {
  const r = row as ClientRow
  if (!confirm(t('tabs.clients.confirmDelete', { name: r.name }))) return
  deleteMutation.mutate(r.id, {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
      if (clientCardStore.selectedClientId === r.id) clientCardStore.clearSelection()
    },
  })
}

function onRowEditHandler(row: unknown) {
  const c = (row as ClientRow)._raw
  const rawGender = c.gender?.trim().toUpperCase()
  const gender: 'MALE' | 'FEMALE' = rawGender === 'MALE' || rawGender === 'M' ? 'MALE' : 'FEMALE'
  clearInfoViewSelections('addClientForm')
  addClientFormStore.openForEdit(c.id, {
    name: c.name ?? '',
    gender,
    phone: c.phone ?? null,
    email: c.email ?? null,
    roleIds: (c.roles ?? []).map((r) => r.id),
    startDatetime: c.start_datetime ?? null,
    birthday: null,
  })
}

// ── Groups pane ──────────────────────────────────────────────────────────────

interface GroupRow {
  // Row for the groups table, mapped from GET `/groups`.

  id: string
  rowNumber: number
  displayName: string
  startedLabel: string
  start: string
  active: boolean
  scheduleLines: string[]
  room: string | null
  performers: { id: string; name: string; gender: 'm' | 'f' | string }[]
  serviceTypeLabels: string[]
  _raw: Group
}

const { displayName: groupDisplayName, scheduleLines: groupScheduleLines } = useGroupDisplay()
/*
 * Room/performers/service-type resolution each hit a catalog endpoint
 * gated on a role the Groups pane itself doesn't require (reachable via
 * ClientsView/GroupsView alone, e.g. Finance) — disabled when absent so a
 * 403 doesn't trip the shared axios interceptor's app-wide navigation.
 */
const canViewBookingTypes = computed(() => hasRole(ResourceAccessRole.BookingTypesView))
const canViewPerformers = computed(() => hasRole(ResourceAccessRole.StuffView))
const canViewServiceTypes = computed(() => hasRole(ResourceAccessRole.BusinessServiceTypesView))
const { bookingTypes } = useAllBookingTypes({ enabled: canViewBookingTypes })
const { performers: allPerformers } = useAllPerformers({ enabled: canViewPerformers })
const { businessServiceTypes: allServiceTypes } = useAllBusinessServiceTypes({
  enabled: canViewServiceTypes,
})

function roomForGroup(g: Group): string | null {
  if (!g.has_dedicated_schedule || g.booking_type_id === null) return null
  return bookingTypes.value.find((bt) => bt.id === g.booking_type_id)?.address ?? null
}

function performersForGroup(g: Group): GroupRow['performers'] {
  const byId = new Map(allPerformers.value.map((p) => [p.id, p]))
  return g.performer_ids
    .map((id) => byId.get(id))
    .filter((p): p is NonNullable<typeof p> => p !== undefined)
    .map((p) => ({ id: p.id, name: p.name, gender: genderLetter(p.gender) }))
}

function serviceTypeLabelsForGroup(g: Group): string[] {
  const byId = new Map(allServiceTypes.value.map((type) => [type.id, type]))
  return g.business_service_type_id
    ? [byId.get(g.business_service_type_id)?.description].filter(
        (label): label is string => !!label && label.trim() !== '',
      )
    : []
}

const groupColumns = computed<Column<GroupRow>[]>(() => [
  {
    key: 'rowNumber',
    label: '#',
    component: markRaw(RowIndexCell),
    props: (row) => ({ index: row.rowNumber }),
    width: '3rem',
    align: 'center',
  },
  {
    key: 'name',
    label: t('tabs.clients.colGroup'),
    component: markRaw(GroupNameCell),
    props: (row) => ({ name: row._raw.name, description: row._raw.description, start: row.start }),
    width: '19%',
  },
  {
    key: 'schedule',
    label: t('tabs.clients.colSchedule'),
    component: markRaw(GroupScheduleCell),
    props: (row) => ({ lines: row.scheduleLines }),
    width: '8.5rem',
  },
  {
    key: 'room',
    label: t('tabs.clients.colRoom'),
    component: markRaw(GroupRoomCell),
    props: (row) => ({ room: row.room }),
    width: '13%',
  },
  {
    key: 'performers',
    label: t('tabs.clients.colPerformers'),
    component: markRaw(GroupPerformersCell),
    props: (row) => ({ performers: row.performers }),
    width: '19%',
  },
  {
    key: 'serviceTypes',
    label: t('tabs.clients.colServiceTypes'),
    component: markRaw(GroupServiceTypesCell),
    props: (row) => ({ labels: row.serviceTypeLabels }),
  },
  {
    key: 'state',
    label: t('tabs.clients.colState'),
    component: markRaw(GroupStateCell),
    props: (row) => ({ active: row.active }),
    width: '6.5rem',
    align: 'center',
  },
])

const groupMobileColumns: Column<GroupRow>[] = [
  {
    key: 'name',
    label: '',
    component: markRaw(GroupMobileCell),
    props: (row) => ({
      displayName: row.displayName,
      startedLabel: row.startedLabel,
      room: row.room,
      active: row.active,
      performers: row.performers,
      scheduleLines: row.scheduleLines,
      serviceTypeLabels: row.serviceTypeLabels,
    }),
  },
]

const {
  data: groupsQueryData,
  isPending: groupsIsPending,
  isError: groupsIsError,
  error: groupsError,
  fetchNextPage: fetchNextGroupsPage,
  hasNextPage: groupsHasNextPage,
  isFetchingNextPage: groupsIsFetchingNextPage,
} = useInfiniteQuery({
  queryKey: ['groups'],
  queryFn: ({ pageParam }) => fetchGroups(pageParam),
  initialPageParam: undefined as number | undefined,
  getNextPageParam: (lastPage) => (lastPage.has_next_page ? lastPage.next_cursor : undefined),
  // GET /groups needs GroupsView; without it a 403 would trip the axios interceptor's app-wide navigation.
  enabled: computed(() => hasRole(ResourceAccessRole.GroupsView)),
  refetchInterval: groupsPollingInterval,
  refetchIntervalInBackground: false,
  placeholderData: keepPreviousData,
})

const groupsData = computed<GroupRow[]>(() => {
  const pages = groupsQueryData.value?.pages
  if (!pages?.length) return []
  let index = 0
  return pages.flatMap((page) =>
    (page.items ?? []).map((g) => {
      index += 1
      const started = formatStartDate({ date: g.start, timeZone: 'UTC' })
      return {
        id: g.id,
        rowNumber: index,
        displayName: groupDisplayName(g),
        startedLabel: t('cards.group.startedOn', {
          date: started.formattedDate,
          since: started.durationSince,
        }),
        start: g.start,
        active: isGroupActive(g),
        scheduleLines: groupScheduleLines(g.schedule),
        room: roomForGroup(g),
        performers: performersForGroup(g),
        serviceTypeLabels: serviceTypeLabelsForGroup(g),
        _raw: g,
      }
    }),
  )
})

function groupRowKeyFn(row: unknown): string {
  return (row as GroupRow).id
}

const selectedGroupRowKey = computed<string | null>(() => groupCardStore.selectedGroupId)

function onGroupRowClick(row: unknown): void {
  clearInfoViewSelections('group')
  groupCardStore.selectGroup((row as GroupRow).id)
}

function onAddGroup(): void {
  clearInfoViewSelections('addGroupForm')
  addGroupFormStore.open()
}

const groupsQueryClient = useQueryClient()
const deleteGroupMutation = useMutation({ mutationFn: (id: string) => deleteGroup(id) })

function onGroupRowDeleteHandler(row: unknown): void {
  const r = row as GroupRow
  if (!confirm(t('tabs.clients.confirmDeleteGroup', { name: r.displayName }))) return
  deleteGroupMutation.mutate(r.id, {
    onSuccess: () => {
      void groupsQueryClient.invalidateQueries({ queryKey: ['groups'] })
      if (groupCardStore.selectedGroupId === r.id) groupCardStore.clearSelection()
    },
  })
}

function onGroupRowEditHandler(row: unknown): void {
  const g = (row as GroupRow)._raw
  clearInfoViewSelections('addGroupForm')
  addGroupFormStore.openForEdit(g.id, {
    name: g.name,
    description: g.description,
    start: g.start,
    end: g.end,
    hasDedicatedSchedule: g.has_dedicated_schedule,
    bookingTypeId: g.booking_type_id,
    businessServiceTypeId: g.business_service_type_id,
    performerIds: g.performer_ids ?? [],
    schedule: g.schedule ?? [],
  })
}
</script>

<template>
  <article>
    <div v-if="isMobile" ref="mobileScrollSentinel" class="scroll-sentinel" aria-hidden="true" />
    <div
      ref="headerRef"
      class="tab-header"
      :class="{
        'tab-header--mobile': isMobile,
        'tab-header--mobile-scrolled': isMobileScrolled,
      }"
    >
      <div
        v-if="!isMobileScrolled"
        class="pane-tabs"
        role="group"
        :aria-label="t('tabs.clients.paneGroupLabel')"
      >
        <button
          type="button"
          class="pane-tab"
          :class="{ 'pane-tab--active': activePane === 'clients', 'pane-tab--mobile': isMobile }"
          @click="activePane = 'clients'"
        >
          {{ t('tabs.clients.title') }}
        </button>
        <button
          v-if="hasRole(ResourceAccessRole.GroupsView)"
          type="button"
          class="pane-tab"
          :class="{ 'pane-tab--active': activePane === 'groups', 'pane-tab--mobile': isMobile }"
          @click="activePane = 'groups'"
        >
          {{ t('tabs.clients.groupsTab') }}
        </button>
      </div>
      <AddButton
        v-if="
          !isMobileScrolled &&
          ((activePane === 'clients' && hasRole(ResourceAccessRole.ClientsCreate)) ||
            (activePane === 'groups' && hasRole(ResourceAccessRole.GroupsCreate)))
        "
        :label="activePane === 'clients' ? t('tabs.clients.addClient') : t('tabs.clients.addGroup')"
        @click="activePane === 'clients' ? onAddClient() : onAddGroup()"
      />
      <button
        v-if="
          isMobileScrolled &&
          ((activePane === 'clients' && hasRole(ResourceAccessRole.ClientsCreate)) ||
            (activePane === 'groups' && hasRole(ResourceAccessRole.GroupsCreate)))
        "
        class="mobile-compact-add"
        type="button"
        :aria-label="
          activePane === 'clients' ? t('tabs.clients.addClient') : t('tabs.clients.addGroup')
        "
        @click="activePane === 'clients' ? onAddClient() : onAddGroup()"
      >
        +
      </button>
    </div>

    <template v-if="activePane === 'clients'">
      <div v-if="isMobile" class="mobile-search-bar">
        <TableSearchInput
          v-model="searchQuery"
          :placeholder="t('tabs.clients.searchPlaceholder')"
        />
      </div>
      <div
        v-if="isMobile"
        class="mobile-filter-chips"
        role="group"
        :aria-label="t('tabs.clients.filterGroupLabel')"
      >
        <button
          v-for="opt in CLIENT_FILTERS"
          :key="opt.value"
          type="button"
          class="filter-chip"
          :class="{ 'filter-chip--active': activeFilter === opt.value }"
          @click="activeFilter = opt.value"
        >
          {{ opt.label }}
        </button>
      </div>
      <div v-if="!isMobile" class="filter-bar">
        <TableSearchInput
          v-model="searchQuery"
          :placeholder="t('tabs.clients.searchPlaceholder')"
        />
        <div class="segmented" role="group" :aria-label="t('tabs.clients.filterGroupLabel')">
          <button
            v-for="opt in CLIENT_FILTERS"
            :key="opt.value"
            type="button"
            :class="{ 'segmented__btn--active': activeFilter === opt.value }"
            @click="activeFilter = opt.value"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>
      <p v-if="isPending" class="text-tufte-muted">{{ t('tabs.clients.loading') }}</p>
      <p v-else-if="isError" class="text-red-600">
        {{
          t('tabs.clients.loadError', {
            message: (error as Error)?.message ?? t('common.unknownError'),
          })
        }}
      </p>
      <DynamicTable
        v-else
        :class="isMobile ? 'clients-table--mobile' : 'clients-table'"
        :columns="isMobile ? (mobileColumns as Column<unknown>[]) : (columns as Column<unknown>[])"
        :data="data"
        :has-next-page="hasNextPage"
        :is-fetching-next-page="isFetchingNextPage"
        :on-load-more="fetchNextPage"
        :on-row-click="onRowClick"
        :row-key="rowKeyFn"
        :selected-row-key="isMobile ? null : selectedRowKey"
        :on-row-edit="
          isMobile || !hasRole(ResourceAccessRole.ClientsEdit) ? undefined : onRowEditHandler
        "
        :on-row-delete="
          isMobile || !hasRole(ResourceAccessRole.ClientsDelete) ? undefined : onRowDeleteHandler
        "
        :on-add="isMobile || !hasRole(ResourceAccessRole.ClientsCreate) ? undefined : onAddClient"
        :pinnable-header="!isMobile"
        :compact-add-visible="!isMobile && !isHeaderVisible"
        storage-key="clients"
      />
    </template>

    <template v-else>
      <p v-if="groupsIsPending" class="text-tufte-muted">{{ t('tabs.clients.loadingGroups') }}</p>
      <p v-else-if="groupsIsError" class="text-red-600">
        {{
          t('tabs.clients.loadErrorGroups', {
            message: (groupsError as Error)?.message ?? t('common.unknownError'),
          })
        }}
      </p>
      <DynamicTable
        v-else
        :class="isMobile ? 'groups-table--mobile' : 'groups-table'"
        :columns="
          isMobile ? (groupMobileColumns as Column<unknown>[]) : (groupColumns as Column<unknown>[])
        "
        :data="groupsData"
        :has-next-page="groupsHasNextPage"
        :is-fetching-next-page="groupsIsFetchingNextPage"
        :on-load-more="fetchNextGroupsPage"
        :on-row-click="onGroupRowClick"
        :row-key="groupRowKeyFn"
        :selected-row-key="isMobile ? null : selectedGroupRowKey"
        :on-row-edit="
          isMobile || !hasRole(ResourceAccessRole.GroupsEdit) ? undefined : onGroupRowEditHandler
        "
        :on-row-delete="
          isMobile || !hasRole(ResourceAccessRole.GroupsDelete)
            ? undefined
            : onGroupRowDeleteHandler
        "
        :on-add="isMobile || !hasRole(ResourceAccessRole.GroupsCreate) ? undefined : onAddGroup"
        :pinnable-header="!isMobile"
        :compact-add-visible="!isMobile && !isHeaderVisible"
        storage-key="groups"
      />
    </template>

    <ClientCardMobileScreen
      v-if="isMobile && activePane === 'clients' && clientCardStore.hasSelection"
      :client-id="clientCardStore.selectedClientId!"
      @close="clientCardStore.clearSelection()"
    />
    <GroupMobileScreen
      v-if="isMobile && activePane === 'groups' && groupCardStore.hasSelection"
      :group-id="groupCardStore.selectedGroupId!"
      @close="groupCardStore.clearSelection()"
    />
  </article>
</template>

<style scoped>
.tab-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

/*
 * Pane switch reads as a page heading, not a filter chip — same
 * font/size/weight as every other tab's `<h1>` title, two side by side.
 * Matches BookingTab's identical pane-tabs convention (see its own comment
 * for why: color-only distinction, no green-pill background from the
 * design sketch, to stay visually consistent with the rest of the app).
 */
.pane-tabs {
  display: flex;
  align-items: baseline;
  gap: 1.6rem;
  flex-wrap: wrap;
}
.pane-tab {
  appearance: none;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-tufte);
  font-size: var(--text-tufte-4xl);
  font-weight: 400;
  line-height: var(--line-height-tufte-tight);
  color: var(--color-tufte-muted);
}
.pane-tab:hover {
  color: var(--color-tufte-text);
}
.pane-tab--active {
  color: var(--color-tufte-text);
}
.pane-tab--mobile {
  font-size: 1.5rem;
}

.scroll-sentinel {
  height: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
}

.filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  /*
   * `nowrap` (was `wrap`): Russian filter labels ("Активные · последние 2
   * мес.", "Отрицательный баланс") are much wider than their English
   * originals, so with `wrap` the segmented control dropped to its own row
   * below the search box once the two together no longer fit — the search
   * input shrinks first instead (see `.filter-bar :deep(.table-search-input)`
   * below) so both stay on one line like before.
   */
  flex-wrap: nowrap;
  gap: 1rem;
  padding: 0.6rem 0;
  margin-bottom: 0.25rem;
  border-bottom: 1px solid var(--color-tufte-border);
}

/* Shrinks first (down to `min-width`) so the segmented control — `flex-shrink: 0` below — never has to give up its own line. */
.filter-bar :deep(.table-search-input) {
  flex: 1 1 auto;
  min-width: 8rem;
  max-width: 18rem;
}

.segmented {
  display: inline-flex;
  flex-shrink: 0;
  border: 1px solid var(--color-tufte-border);
  border-radius: 6px;
  overflow: hidden;
}

.segmented button {
  appearance: none;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.02em;
  color: var(--color-tufte-muted);
  background: var(--color-tufte-bg);
  border: none;
  border-right: 1px solid var(--color-tufte-border);
  padding: 0.4rem 0.85rem;
  white-space: nowrap;
  cursor: pointer;
}

.segmented button:last-child {
  border-right: none;
}

.segmented button:hover:not(.segmented__btn--active) {
  background: color-mix(in srgb, var(--color-tufte-border) 25%, var(--color-tufte-bg));
}

.segmented button.segmented__btn--active {
  background: color-mix(in srgb, var(--color-tufte-text) 8%, transparent);
  color: var(--color-tufte-text);
}

.mobile-search-bar {
  padding: 0.6rem 1.25rem 0;
}

.mobile-filter-chips {
  display: flex;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  overflow-x: auto;
}

.filter-chip {
  appearance: none;
  flex-shrink: 0;
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.01em;
  color: var(--color-tufte-muted);
  background: none;
  border: 1px solid var(--color-tufte-border);
  border-radius: 999px;
  padding: 0.35rem 0.85rem;
  white-space: nowrap;
  cursor: pointer;
}

.filter-chip--active {
  background: color-mix(in srgb, var(--color-tufte-text) 7%, transparent);
  color: var(--color-tufte-text);
  border-color: color-mix(in srgb, var(--color-tufte-text) 45%, transparent);
}

.tab-header--mobile {
  padding: 0.75rem 1.25rem;
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--color-tufte-bg);
  border-bottom: 1px solid var(--color-tufte-border);
}

.tab-header--mobile h1 {
  margin-top: 0;
  margin-bottom: 0;
}

.tab-header--mobile-scrolled {
  padding: 0.4rem 1.25rem;
  justify-content: flex-end;
}

.mobile-compact-add {
  appearance: none;
  background: transparent;
  border: 1px solid var(--color-tufte-border);
  border-radius: 50%;
  width: 1.75rem;
  height: 1.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  line-height: 1;
  padding: 0;
  color: var(--color-tufte-muted);
  cursor: pointer;
}

.mobile-compact-add:hover,
.mobile-compact-add:active {
  color: var(--color-tufte-text);
  border-color: var(--color-tufte-text);
}

.mobile-compact-add:focus-visible {
  outline: none;
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--color-tufte-text) 40%, transparent);
}

/* Left cell: tighter left edge so the index sits near the screen margin. */
.clients-table--mobile :deep(.client-mobile-left) {
  padding-left: 0.5rem;
}

/* Mobile table: full-width, no thead, tighter horizontal padding. */
.clients-table--mobile :deep(.dynamic-table) {
  width: 100%;
}

.clients-table--mobile :deep(thead) {
  display: none;
}

.clients-table--mobile :deep(td) {
  padding: 0.7rem 1.25rem;
}

/* Mobile groups table: full-width, no thead, denser padding than the
 * clients mobile table since each row is a multi-line card. */
.groups-table--mobile :deep(.dynamic-table) {
  width: 100%;
}

.groups-table--mobile :deep(thead) {
  display: none;
}

.groups-table--mobile :deep(td) {
  padding: 0.75rem 1.25rem 0.8rem;
}
</style>
